/**
 * NetMessage layer — the game messages wrapped inside XMSG_GAMENETMESSAGE (0x54),
 * plus the optimized XMSG_GAMEUPDATEEMPTY (0x55) and XMSG_GAMEUPDATERESULT (0x56) frames.
 * Port of mpshared/netmessage.{h,cpp} + the GAMENETMESSAGE (un)wrapping in messages.cpp.
 *
 * WIRE FORMAT of XMSG_GAMENETMESSAGE (messages.cpp:205-245):
 *   [u16 size][u8 0x54][u8 nmid][inner payload]
 * where `inner payload` is the NetMessage struct MINUS its 4-byte {cb,nmid} header, with
 * multi-byte fields byte-swapped to network order by NetMessageByteOrderSwap.
 * (ScUpdate/CsUpdateResult are special-cased to 0x55/0x56 by ToBuffer and are NOT wrapped.)
 *
 * THIS MODULE (Step 1) decodes to the WRAPPER level: it exposes nmid + the raw inner
 * payload, which is sufficient for byte-exact round-trip and for routing. Per-struct field
 * decoding (GameParams fields, the 28-byte Message command array, PlayersUpdate records,
 * WinStats) + the field-by-field byte-swap is built in Step 5, where the DO must assemble
 * ScUpdate ticks — and is validated against the same captured game vectors.
 */
import { ByteReader, ByteWriter } from './bytebuffer.ts';
import { XMSG, XMSG_FIXED, NMID_NAME } from './constants.ts';

export interface GameNetMessage {
  id: typeof XMSG.GAMENETMESSAGE; // 0x54
  kind: 'gamenetmessage';
  nmid: number;
  inner: Uint8Array; // raw inner payload (network byte order), field-decode in Step 5
  nmidName?: string;
}

export interface GameUpdateEmpty {
  id: typeof XMSG.GAMEUPDATEEMPTY; // 0x55
  kind: 'gameupdateempty';
  cUpdatesBlock: number;
  cUpdatesSync: number;
}

export interface GameUpdateResult {
  id: typeof XMSG.GAMEUPDATERESULT; // 0x56
  kind: 'gameupdateresult';
  cUpdatesBlock: number;
  hash: number;
  cmsLatency: number; // signed 16-bit on the wire (messages.cpp clamps to short)
}

export type GameMessage = GameNetMessage | GameUpdateEmpty | GameUpdateResult;

export function isGameId(id: number): boolean {
  return id === XMSG.GAMENETMESSAGE || id === XMSG.GAMEUPDATEEMPTY || id === XMSG.GAMEUPDATERESULT;
}

/** Decode a game-layer frame (0x54/0x55/0x56). `buf` is the full framed XMsg. */
export function decodeGame(buf: Uint8Array): GameMessage {
  const r = new ByteReader(buf);
  const size = r.readWord();
  if (size !== buf.length) throw new Error(`game frame size ${size} != ${buf.length}`);
  const id = r.readByte();

  if (id === XMSG.GAMENETMESSAGE) {
    const nmid = r.readByte();
    const inner = r.readBytes(r.remaining);
    return { id, kind: 'gamenetmessage', nmid, inner, nmidName: NMID_NAME[nmid] };
  }
  if (id === XMSG.GAMEUPDATEEMPTY) {
    return {
      id,
      kind: 'gameupdateempty',
      cUpdatesBlock: r.readDword(),
      cUpdatesSync: r.readDword(),
    };
  }
  if (id === XMSG.GAMEUPDATERESULT) {
    const cUpdatesBlock = r.readDword();
    const hash = r.readDword();
    const raw = r.readWord();
    const cmsLatency = raw >= 0x8000 ? raw - 0x10000 : raw; // signed short
    return { id, kind: 'gameupdateresult', cUpdatesBlock, hash, cmsLatency };
  }
  throw new Error(`decodeGame: not a game frame id 0x${id.toString(16)}`);
}

/** Encode a game-layer frame back to exact wire bytes. */
export function encodeGame(msg: GameMessage): Uint8Array {
  const body = new ByteWriter();
  if (msg.kind === 'gamenetmessage') {
    body.writeByte(msg.nmid);
    body.writeBytes(msg.inner);
  } else if (msg.kind === 'gameupdateempty') {
    body.writeDword(msg.cUpdatesBlock);
    body.writeDword(msg.cUpdatesSync);
  } else {
    body.writeDword(msg.cUpdatesBlock);
    body.writeDword(msg.hash);
    const l = msg.cmsLatency < 0 ? msg.cmsLatency + 0x10000 : msg.cmsLatency;
    body.writeWord(l & 0xffff);
  }
  const out = new ByteWriter();
  out.writeWord(XMSG_FIXED + body.length);
  out.writeByte(msg.id);
  out.writeBytes(body.toUint8Array());
  return out.toUint8Array();
}
